import { useImmer } from "use-immer";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { Skeleton } from "@mui/material";

import { DiscardChangesDialog } from "@src/features/agenticCodegen/workspace/components/00-PageFrame/DiscardChangesDialog";
import { WorkspacePageFrame } from "@src/features/agenticCodegen/workspace/components/00-PageFrame/WorkspacePageFrame";

import { GraphBuildingState } from "../graph/GraphBuildingState";
import { useAgenticLogicSurface } from "../surface/useAgenticLogicSurface";
import { useSurfaceNavigate } from "../surface/useSurfaceNavigate";
import { RULE_DETAIL_TABS } from "../workspace/ruleDetailTabs";

const REDUCED_MOTION = "@media (prefers-reduced-motion: reduce)";

const skeletonSx = {
  [REDUCED_MOTION]: { animation: "none" },
} as const;

/**
 * One line of the unwritten description: the same thin gray/07 pill the canvas
 * cards shimmer with. A text-variant skeleton carries its font's whole line
 * box, which left the two lines floating far apart from each other and from
 * the name above them.
 */
const SummaryBar = ({ width }: { width: number }) => (
  <Skeleton
    aria-hidden
    variant="rounded"
    width={width}
    height={7}
    sx={(theme) => ({
      backgroundColor: theme.igPrimitives.gray[600],
      borderRadius: "999px",
      ...skeletonSx,
    })}
  />
);

/**
 * The New Rule view while an Atlas build is running: the rule workspace the
 * build is about to land on, sketched in skeletons. Same frame, same tab bar,
 * same canvas — so the moment the create tool navigates to the real rule, the
 * page fills in rather than changing shape.
 *
 * The chrome is scenery: the name and description are shimmer bars, the tab
 * bar is pinned to Selection logic, and the canvas shows the placeholder
 * skeleton graph. Save changes renders as a real, disabled button because
 * there is nothing yet to save. Only "All rules" is live — leaving to the list
 * is the existing bail-out that ends the session (`LoggedInProvider`).
 */
export const CreateRuleDesigningState = () => {
  const navigate = useSurfaceNavigate();
  const surface = useAgenticLogicSurface();
  const [confirmingExit, setConfirmingExit] = useImmer(false);

  return (
    <>
      <DiscardChangesDialog
        open={confirmingExit}
        mode="leave"
        entityLabel="rule"
        leaveSubtitle="Atlas is still creating this rule. You can leave, but the current build will stop."
        onKeepEditing={() => setConfirmingExit(false)}
        onDiscard={() => {
          setConfirmingExit(false);
          navigate(surface.basePath);
        }}
      />
      <WorkspacePageFrame
        embedded
        backLabel="All rules"
        onBack={() => setConfirmingExit(true)}
        title={
          <Skeleton
            aria-hidden
            variant="text"
            width={240}
            sx={{ fontSize: 24, ...skeletonSx }}
          />
        }
        meta={<SummaryBar width={390} />}
        summary={<SummaryBar width={280} />}
        actions={
          <IgButton
            color="primary"
            disabled
            variant="contained"
            sx={{ height: 40, minWidth: 100, px: 4 }}
          >
            Save changes
          </IgButton>
        }
        tabs={RULE_DETAIL_TABS}
        selectedTab="selection_logic"
        // The tabs are part of the sketch, not navigation: there is nothing
        // behind Advanced until the rule exists.
        onSelectedTabChange={() => undefined}
      >
        <GraphBuildingState
          title="Atlas is designing this rule"
          hint="Follow along in the chat — this becomes the rule's workspace the moment it exists."
          showHeading={false}
        />
      </WorkspacePageFrame>
    </>
  );
};
